<script setup lang="ts">
import {computed, inject, nextTick, reactive, ref, useTemplateRef, watch} from "vue";
import {useIntersectionObserver, useScroll} from "@vueuse/core";
import User from "@/Models/User";
import {Editor} from "@/Components/Forms";
import {
    Avatar,
    Button,
    Dialog,
    FileUpload,
    Icon,
    ImportantInfoPanel,
    Menu,
    Skeleton,
    Textarea,
} from "@/Components/Library";
import type {FileUploadSelectEvent} from "primevue/fileupload";
import type dayjs from "dayjs";
import ParticipantsListDialog from "@/Pages/Conversations/ParticipantsListDialog.vue";
import ConversationMessage from "@/Pages/Conversations/ConversationMessage.vue";
import useEcho from "@/Composables/echo";
import {useToast} from "primevue/usetoast";
import {deleteConversation, getConversation as getConversationFromApi} from "@/Services/conversations";
import {useRoute, useRouter} from "vue-router";
import ConversationEvents from "./conversation-events";
import {
    removeConversationParticipant,
    storeConversationParticipants,
    toggleConversationParticipant,
} from "@/Services/conversation-participants";
import {getConversationMessages, storeConversationMessage} from "@/Services/conversation-messages";
import {ContactDrawer, PageTitle} from "@/Components/Global";
import ConversationFormDialog from "@/Pages/Conversations/ConversationFormDialog.vue";
import GroupMemberDrawer from "@/Pages/User/Groups/GroupMemberDrawer.vue";
import {useConfirm} from "primevue/useconfirm";
import {useMainStore} from "@/Stores/store";

const $date = inject("date") as typeof dayjs;
const $user = new User();
const $store = useMainStore();
const confirm = useConfirm();
const toast = useToast();
const router = useRouter();
const currentRoute = useRoute();

type ConversationLocal = any;
type ConversationParticipant = any;

const state = reactive({
    richText: false,
    loading: {
        sending: false,
        conversation: true,
        participants: false,
        more: false,
    },
    show: {
        participants: false,
        attach: false,
        scrollDown: false,
        participantMenu: false,
        contact: null,
        contactDrawer: false,
        conversationForm: false,
    },
    conversation: {
        participants: [] as Array<ConversationParticipant>,
    },
});
const newMessage = reactive({
    id: "0",
    body: "",
});

const conversation = ref(null);
const isArchived = ref(false);
const isAdmin = computed(
    () =>
        $user.hasRole("admin") ||
        $user.hasRole("super-admin") ||
        (conversation.value?.group ? conversation.value.group.can_admin : conversation.value?.user_id === $user.id),
);
const visibleParticipants = ref([]);
const messages = ref([]);
const messagesPane = useTemplateRef("messagesPane");
const {x, y, isScrolling, arrivedState, directions} = useScroll(messagesPane, {behavior: "smooth"});
const showScrollDown = computed(() => {
    return !arrivedState.bottom;
});
const messageRefs = ref([]);
const attachmentsFileUpload = ref<typeof FileUpload | null>(null);
const page = ref(1);
const totalMessages = ref(conversation.value?.messages_count);

const getConversation = async () => {
    try {
        state.loading.conversation = true;
        const {data} = await getConversationFromApi(currentRoute.params.conversation);
        if (data.unread_message_count > 0) {
            $store.decrementUnreadConversations();
        }

        conversation.value = data;
        messages.value = data.messages || [];
        scrollToEnd("auto");
        isArchived.value = data.participants?.find((p) => p.id === $user.id)?.pivot?.is_archived;
        visibleParticipants.value = data.participants?.filter((p) => !p.pivot?.is_hidden || isAdmin.value) || [];
    } catch (error) {
        console.log(error);
    } finally {
        state.loading.conversation = false;
    }
};
getConversation();

//TODO: only load more if count < total
const loadMoreIntersect = useTemplateRef("loadMoreIntersect");
const {stop} = useIntersectionObserver(
    loadMoreIntersect,
    ([{isIntersecting}], observerElement) => isIntersecting && loadMore(),
    {
        // immediate: false, // This prevents immediate execution
    },
);

const scrollToEnd = async (behavior = "smooth") => {
    await nextTick();
    const scrollHeight = Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);
    window.scrollTo({
        top: scrollHeight,
        behavior: behavior,
    });
};

const loadMore = async () => {
    if (!messagesPane.value) {
        return;
    }

    try {
        state.loading.more = true;
        let oldScrollHeight = document.documentElement.scrollHeight;
        const resp = await getConversationMessages(currentRoute.params.conversation, {page: ++page.value});
        messages.value = [...resp.data, ...messages.value];
        totalMessages.value = resp.total;
        await nextTick();
        let newScrollHeight = document.documentElement.scrollHeight;
        window.scrollTo(0, newScrollHeight - oldScrollHeight);
    } catch (error) {
        console.log(error);
    } finally {
        state.loading.more = false;
    }
};

const selectedContact = computed(() =>
    conversation.value?.participants.find((contact) => contact.id === state.show.contact),
);

const sendButton = ref<HTMLElement | null>(null);
const isRecording = ref(false);
const startRecording = async () => {
    isRecording.value = true;
    console.log("startRecording");
    const constraints = {
        video: false,
        audio: {
            channelCount: 1,
            echoCancellation: false,
        },
    };

    try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        console.log(stream);
        const mediaRecorder = new MediaRecorder(stream);
    } catch (e) {
        console.log(e);
    }
};
// onLongPress(sendButton, startRecording, {
//     delay: 500,
//     // modifiers: {
//     //     prevent: true,
//     // },
// });

const isSameDate = (message: ConversationLocal["messages"][number], ind: number) => {
    return (
        ind > 0 &&
        $date(message.created_at).format("YYYYMMDD") === $date(messages.value[ind - 1].created_at).format("YYYYMMDD")
    );
};
const isSameSender = (message: ConversationLocal["messages"][number], ind: number) => {
    return ind > 0 && message.sender?.id === messages.value[ind - 1]?.sender?.id;
};

const addParticipants = async (newParticipants: Array<ConversationParticipant>) => {
    try {
        const {data} = await storeConversationParticipants(currentRoute.params.conversation, {
            participants: newParticipants.map((p) => p.id),
        });
        conversation.value.participants = [...conversation.value.participants, ...data];
        visibleParticipants.value = conversation.value.participants.filter((p) => !p.pivot?.is_hidden || isAdmin.value);
        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Participants added successfully",
            life: 3000,
        });
        state.show.participants = false;
    } catch (error) {
        console.log(error);
    }
};

const toggleHideParticipant = async (id?: string) => {
    try {
        if (!id) {
            return;
        }

        const newValue = !conversation.value?.participants.find((p) => p.id === id)?.pivot?.is_hidden;
        const {data} = await toggleConversationParticipant(currentRoute.params.conversation, id, {
            property: "is_hidden",
            value: newValue,
        });
        conversation.value.participants.find((p) => p.id === id).pivot.is_hidden = newValue;
        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Participant hidden successfully",
            life: 3000,
        });
    } catch (error) {
        console.log(error);
    }
};

const removeParticipant = async (id?: string) => {
    try {
        if (!id) {
            return;
        }

        await removeConversationParticipant(currentRoute.params.conversation, id);
        conversation.value.participants = conversation.value?.participants.filter((p) => p.id !== id);
        visibleParticipants.value = conversation.value.participants.filter((p) => !p.pivot?.is_hidden || isAdmin.value);
        toast.add({
            severity: "success",
            summary: "Success",
            detail: "Participant removed successfully",
            life: 3000,
        });
    } catch (error) {
        console.log(error);
    }
};

const resetMessage = () => {
    newMessage.body = "";
    newMessage.attributes = {
        attachments: [],
    };
};

const sendMessage = async () => {
    try {
        state.loading.sending = true;
        if (!isRecording.value) {
            const {data} = await storeConversationMessage(currentRoute.params.conversation, {
                body: newMessage.body,
            });
            console.log("message stored", data);
            newMessage.id = data.id;

            messages.value.push(data);
            await nextTick();
            resetMessage();
            scrollToEnd();

            try {
                //TODO: handle errors, show loading
                console.log(attachmentsFileUpload.value);
                if (attachmentsFileUpload.value?.files?.length) {
                    await nextTick();
                    const resp = await attachmentsFileUpload.value?.startUpload();
                    if (resp) {
                        let attachments = JSON.parse(resp.xhr.responseText);
                        // data.attachments = attachments.data;
                        let message = messages.value.find((m) => m.id === data.id);
                        message.attachments = attachments.data;
                        await nextTick();
                    }
                }
            } catch (e) {
                console.log(e);
                alert("Files were not uploaded. Please try again.");
            }
        } else {
            isRecording.value = false;
            await sendMessage();
        }
    } catch (e) {
        console.log(e);
    } finally {
        state.loading.sending = false;
        state.show.attach = false;
        state.richText = false;
    }
};

const fileUploaded = (e) => {
    console.log(e);
};

const attachmentSelected = (event: FileUploadSelectEvent) => {
    console.log(event.files);
    newMessage.attributes.attachments = event.files;
    return false;
};

const participantMenu = ref<typeof Menu>();
const selectedParticipant = ref<ConversationParticipant | null>(null);
const participantMenuItems = ref([
    {
        label: "",
        items: [],
    },
]);
const showParticipantMenu = computed({
    get: () => !!selectedParticipant.value && !!state.show.participantMenu,
    set: (val) => (state.show.participantMenu = val),
});
const conversationMenuContainer = ref();
const conversationMenuItems = ref([]);
const participantMenuItemsBase = {
    hide: {
        show: () => isAdmin.value,
        label: (participant) => (participant?.pivot.is_hidden ? "Unhide" : "Hide"),
        icon: "mdi:hide-outline",
        command: () => {
            toggleHideParticipant(selectedParticipant?.value?.id);
            state.show.participantMenu = false;
        },
    },
    remove: {
        show: () => true,
        label: (participant) => (participant?.id === $user.id ? "Leave" : "Remove"),
        icon: "mdi:account-remove-outline",
        class: "text-red-500",
        command: () => {
            removeParticipant(selectedParticipant?.value?.id);
            state.show.participantMenu = false;
        },
    },
};

const confirmDeleteConversation = () => {
    confirm.require({
        message: "Are you sure you want to delete this conversation? This action cannot be undone.",
        header: "Confirm Deletion",
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
        accept: async () => {
            try {
                await deleteConversation(conversation.value?.id);
                toast.add({
                    severity: "success",
                    summary: "Success",
                    detail: "Conversation deleted successfully",
                    life: 3000,
                });
                router.replace({name: "conversations.index"});
            } catch (error) {
                console.log(error);
            }
        },
    });
};

const deleteConversationMenuItem = {
    label: "Delete",
    icon: "prime:trash",
    class: "text-red-600",
    command: () => confirmDeleteConversation(),
};

const toggleMenu = (event: Event, participant: ConversationParticipant) => {
    selectedParticipant.value = participant;

    // Set menu items conditionally
    participantMenuItems.value[0].items = Object.values(participantMenuItemsBase)
        .filter((item) => item.show())
        .map((item) => ({
            label: item.label(participant),
            icon: item.icon,
            class: item.class,
            command: item.command,
        }));

    participantMenuItems.value[0].label = participant.first_name + " " + participant.last_name;

    state.show.participantMenu = true;
};

const toggleConversationMenu = (event: Event) => {
    conversationMenuItems.value = [deleteConversationMenuItem];
    conversationMenuContainer.value?.menu.toggle(event);
};

const handleDeleteMessage = (ind: number, deleteCount: number) => {
    if (isAdmin.value) {
        return;
    }

    messages.value.splice(ind, deleteCount);
};

const handleRestoreMessage = (message: ConversationMessage) => {
    const restoreIndex = messages.value.findIndex((m) => m.id === message.id);
    if (restoreIndex === -1) {
        return;
    }

    messages.value[restoreIndex].deleted_at = null;
};

const handleDeleteAttachment = (message: ConversationMessage, attachment: {id: string}) => {
    // TODO: Also use this by echo's event for deleting attachments? If we'll add one
    const messageWithDeletedAttachment = messages.value.find((m) => m.id === message.id);
    if (!messageWithDeletedAttachment) {
        return;
    }

    messageWithDeletedAttachment.attachments = messageWithDeletedAttachment.attachments.filter(
        (a) => a.id !== attachment.id,
    );
};

const showContact = async (contact) => {
    try {
        if (contact.id === "albie") {
            return;
        }

        if (contact.contact_id) {
            //TODO groups you have in common
            // const {data} = await getContact(contact.contact_id);
            // console.log(data);
        }
        state.show.contact = contact.id;
        state.show.contactDrawer = true;
    } catch (e) {
        console.log(e);
    }
};

const handleContactUpdate = (contact) => {
    if (!contact) {
        return;
    }

    // const user = contacts.value.find((c) => c.contact_id === contact.id);
    // user.notes = contact.notes;
    // user.preferences = contact.preferences;
};

const createConversation = () => {
    state.show.contactDrawer = false;
    state.show.conversationForm = true;
};

const handleConversationSaved = (coversation) => {
    state.show.conversationForm = false;
    router.replace({name: "conversations.show", params: {conversation: coversation.id}});
};

useEcho(ConversationEvents($user, conversation, messages, toast, router, getConversation));

watch(() => currentRoute.params.conversation, getConversation);
</script>
<template>
    <div class="absolute top-0 z-10 flex h-full w-full grow flex-col">
        <PageTitle
            :title="conversation?.title || 'Chat'"
            class="m-container sticky top-0 z-50 mb-2 bg-white py-3 dark:bg-stone-950"
        >
            <div class="ml-auto" v-if="conversation">
                <div class="flex items-center">
                    <span class="mr-2 text-red-600">
                        <Icon v-if="conversation.deleted_at" icon="fa-solid:archive" />
                    </span>
                </div>
                <div class="ml-auto flex items-center" v-if="isAdmin">
                    <Button class="mr-2" @click="state.show.participants = true" :icon="{icon: 'fa-solid:users'}" />
                    <Button @click="toggleConversationMenu" :icon="{icon: 'mdi:dots-vertical'}" />
                    <Menu ref="conversationMenuContainer" :popup="true" :model="conversationMenuItems" />
                </div>
            </div>
        </PageTitle>
        <template v-if="conversation">
            <div class="flex">
                <!-- Participants sidebar -->
                <div
                    class="dark:bg-stone--800 bg--white border--r sticky top-0 hidden h-screen w-full min-w-60 max-w-xs flex-col overflow-auto lg:flex dark:border-stone-800"
                >
                    <div class="flex flex-col pt-3">
                        <h3 class="font-display mb-4 px-4 py-1 text-lg font-semibold leading-none">Participants</h3>
                        <div
                            class="border-y px-4 py-6 dark:border-none dark:border-stone-700"
                            :options="{wheelSpeed: 0.5, wheelPropagation: false}"
                            v-auto-animate
                        >
                            <div
                                v-for="(participant, i) in visibleParticipants"
                                :key="participant.id"
                                class="mb-4 flex w-full items-center"
                                :class="{'opacity-50': participant.pivot?.is_hidden}"
                            >
                                <button class="flex grow items-center" @click="() => showContact(participant)">
                                    <Avatar
                                        :user="participant"
                                        class="mr-2.5 flex h-8 w-8 shrink-0 text-xs tracking-wider"
                                        shape="circle"
                                    />
                                    <!--                        <RouterLink-->
                                    <!--                            class="mr-4 text-sm"-->
                                    <!--                            :to="{name: 'contacts.show', params: {contact: participant.id}}"-->
                                    <!--                        >-->
                                    <div class="mr-4 text-sm">
                                        <span v-if="participant.id === $user.id" class="font-medium">Me</span>
                                        <div v-else>
                                            <span class="font-medium">{{ participant.first_name }}</span>
                                            <span class="ml-1.5 font-bold">{{ participant.last_name }}</span>
                                        </div>
                                    </div>
                                </button>
                                <!--                        </RouterLink>-->
                                <div class="ml-auto flex items-center" v-if="participant.id === $user.id || isAdmin">
                                    <Button
                                        class="relative inline-block !p-2 text-left"
                                        @click="toggleMenu($event, participant)"
                                        aria-haspopup="true"
                                        aria-controls="participant_menu"
                                        :icon="{icon: 'mdi:dots-vertical'}"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="flex justify-center">
                            <template v-if="conversation && conversation.group_id">
                                <RouterLink :to="{name: 'groups.show', params: {group: conversation.group_id}}">
                                    <Button class="font h-16 justify-center rounded-none text-sm" text>
                                        <Icon icon="mdi:account-group-outline" width="20" />
                                        <span class="ml-3 font-normal">Go to group</span>
                                    </Button>
                                </RouterLink>
                            </template>
                            <template v-else>
                                <Button
                                    class="font h-16 justify-center rounded-none text-sm"
                                    @click="state.show.participants = true"
                                    text
                                >
                                    <Icon icon="mdi:account-add-outline" width="20" />
                                    <span class="ml-3 font-normal">Add participants</span>
                                </Button>
                            </template>
                        </div>
                    </div>
                </div>
                <div class="flex max-h-full grow flex-col">
                    <!-- Messages -->
                    <div class="relative flex grow flex-col overflow-hidden" v-auto-animate="{duration: 100}">
                        <!--                        <div class="!fixed left-0 right-0 top-0 z-20 flex w-full justify-center px-6">-->
                        <!--                            <div class="mx-auto flex max-w-5xl justify-center xl:mx-0">-->
                        <!--                                <Button-->
                        <!--                                    @click="scrollToEnd"-->
                        <!--                                    class="mx-auto mt-2 flex max-w-max items-center rounded-full bg-stone-800/50 px-3 py-1.5 text-xs normal-case leading-none text-white ring-0"-->
                        <!--                                >-->
                        <!--                                    Scroll down-->
                        <!--                                    <Icon icon="mingcute:down-fill" class="ml-1" />-->
                        <!--                                </Button>-->
                        <!--                            </div>-->
                        <!--                        </div>-->
                        <div
                            class="flex h-full flex-col overflow-auto px-6 pb-8 md:pr-8"
                            ref="messagesPane"
                            v-auto-animate
                        >
                            <div class="mx-auto flex max-w-5xl grow flex-col xl:mx-0">
                                <div v-if="messages.length" class="mb-2">
                                    <span ref="loadMoreIntersect" />
                                    <template v-for="(message, ind) in messages" :key="message.id">
                                        <ConversationMessage
                                            ref="messageRefs"
                                            :isAdmin
                                            :message="message"
                                            :ind="ind"
                                            :isSameDate="isSameDate(message, ind)"
                                            :isSameSender="isSameSender(message, ind)"
                                            @deleted="handleDeleteMessage(ind, 1)"
                                            @restored="handleRestoreMessage"
                                            @attachmentDeleted="handleDeleteAttachment"
                                            @show="showContact"
                                        />
                                    </template>
                                </div>
                                <div v-else class="flex h-full grow flex-col items-center justify-center">
                                    <h3 class="mb-8 text-center text-xl font-semibold">
                                        There are no messages in this chat yet
                                    </h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Message input -->
                    <div
                        class="sticky bottom-0 z-50 w-full border-t bg-white px-8 py-3 dark:border-t-stone-800 dark:bg-stone-950"
                    >
                        <!-- Important Info Panel for Group Chats -->
                        <ImportantInfoPanel
                            v-if="conversation?.group_id && conversation.group?.important_info"
                            :content="conversation.group.important_info"
                            class="mb-4 w-full max-w-5xl"
                        />
                        <div class="mx-auto flex w-full max-w-5xl items-stretch xl:mx-0">
                            <div class="mr-3 flex flex-col py-2">
                                <Button
                                    class="flex h-8 w-8 items-center justify-center rounded-full p-0 hover:bg-stone-200 dark:hover:bg-stone-700"
                                    @click.stop="state.richText = !state.richText"
                                    :icon="{icon: 'fluent:draw-text-24-filled', width: '20'}"
                                />
                                <div class="mt-auto">
                                    <Button
                                        class="mt-auto flex h-8 w-8 items-center justify-center rounded-full p-0 hover:bg-stone-200 dark:hover:bg-stone-700"
                                        @click.stop="state.show.attach = !state.show.attach"
                                        :icon="{icon: 'ic:round-attach-file', width: '20'}"
                                    />
                                </div>
                            </div>
                            <div class="relative grow">
                                <Editor id="message_editor" v-if="state.richText" v-model="newMessage.body" />
                                <Textarea
                                    id="message_body"
                                    v-else
                                    placeholder="Your message..."
                                    class="w-full bg-stone-200"
                                    :rows="3"
                                    autoResize
                                    v-model="newMessage.body"
                                    @keydown.enter.exact.prevent="sendMessage"
                                />
                                <div
                                    class="mt-2 rounded-md border border-stone-300 bg-white dark:border-stone-700 dark:bg-stone-800"
                                    v-if="state.show.attach"
                                >
                                    <FileUpload
                                        ref="attachmentsFileUpload"
                                        id="attachments"
                                        name="attachments"
                                        :fileLimit="10"
                                        multiple
                                        collection="attachment"
                                        parent-type="conversation-message"
                                        hide-upload-button
                                        :parent-id="newMessage.id"
                                        @upload="fileUploaded"
                                    />
                                </div>
                            </div>
                            <div class="ml-3" :class="[state.richText ? 'pt-10' : 'pt-6']">
                                <Button
                                    :disabled="!newMessage.body"
                                    :loading="state.loading.sending"
                                    @mouseup="sendMessage"
                                    ref="sendButton"
                                    class="flex h-12 w-12 items-center justify-center rounded-full bg-askr-green-500 p-2 text-white hover:bg-askr-green-600"
                                    :icon="{icon: 'ic:round-send', width: '24'}"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <ParticipantsListDialog
                v-model="state.show.participants"
                :conversation="conversation"
                @addParticipants="addParticipants"
                @removeParticipant="removeParticipant"
                :showClose="true"
            />
            <Dialog
                v-model:visible="showParticipantMenu"
                modal
                dismissable-mask
                class="max-w-sm overflow-hidden"
                :closable="false"
                :show-header="false"
            >
                <template #container>
                    <div class="bg-white">
                        <Menu ref="participantMenu" :model="participantMenuItems" />
                    </div>
                </template>
            </Dialog>
        </template>
        <!-- Loading skeleton -->
        <template v-else>
            <div class="flex">
                <!-- Participants sidebar skeleton -->
                <div
                    class="dark:bg-stone--800 bg--white border--r sticky top-0 hidden h-screen w-full min-w-60 max-w-xs flex-col overflow-auto lg:flex dark:border-stone-800"
                >
                    <div class="flex flex-col pt-3">
                        <h3 class="font-display mb-4 px-4 py-1 text-lg font-semibold leading-none">Participants</h3>
                        <div class="border-y px-4 py-6 dark:border-none dark:border-stone-700">
                            <div
                                v-for="i in 4"
                                :key="`participant-skeleton-${i}`"
                                class="mb-4 flex w-full items-center"
                            >
                                <Skeleton width="2rem" height="2rem" shape="circle" class="mr-2.5 shrink-0" />
                                <Skeleton width="60%" height="0.875rem" class="grow" />
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Messages skeleton -->
                <div class="flex max-h-full grow flex-col">
                    <div class="relative flex grow flex-col overflow-hidden">
                        <div class="flex h-full flex-col overflow-auto px-6 pb-8 md:pr-8">
                            <div class="mx-auto flex max-w-5xl grow flex-col xl:mx-0">
                                <div class="mb-2 space-y-4 py-6">
                                    <div v-for="i in 6" :key="`message-skeleton-${i}`">
                                        <div v-if="i % 3 === 0" class="mb-4 flex items-center gap-3">
                                            <Skeleton width="8rem" height="0.75rem" class="mx-auto" />
                                        </div>
                                        <div
                                            class="mb-3 flex items-start gap-3"
                                            :class="i % 2 === 0 ? '' : 'flex-row-reverse'"
                                        >
                                            <Skeleton width="2.5rem" height="2.5rem" shape="circle" class="shrink-0" />
                                            <div
                                                class="flex flex-col gap-2"
                                                :class="i % 2 === 0 ? 'items-start' : 'items-end'"
                                            >
                                                <Skeleton
                                                    :width="`${Math.random() * 200 + 200}px`"
                                                    height="3rem"
                                                    border-radius="1rem"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- Message input skeleton -->
                    <div
                        class="sticky bottom-0 z-50 w-full border-t bg-white px-8 py-3 dark:border-t-stone-800 dark:bg-stone-950"
                    >
                        <div class="mx-auto flex w-full max-w-5xl items-center gap-3 xl:mx-0">
                            <Skeleton width="2rem" height="2rem" shape="circle" />
                            <Skeleton width="100%" height="3rem" border-radius="0.5rem" class="grow" />
                            <Skeleton width="3rem" height="3rem" shape="circle" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <ConversationFormDialog
            v-model="state.show.conversationForm"
            :participants="[selectedContact]"
            @saved="handleConversationSaved"
        />
        <GroupMemberDrawer
            v-if="conversation?.group_id"
            v-model="state.show.contactDrawer"
            :member="selectedContact"
            :group="conversation?.group"
        />
        <ContactDrawer
            v-else
            v-model="state.show.contactDrawer"
            :user="selectedContact"
            @contact-updated="handleContactUpdate"
            @create-conversation="createConversation"
        />
    </div>
</template>
